import React, { useState } from "react";
import "./reuseableGallery.css";

const ReusableGallery = ({ heading, images = [], description = "" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  // Navigation handlers
  const goToPrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0
        ? Math.max(0, images.length - 4)
        : Math.max(0, prevIndex - 1)
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex >= images.length - 4 ? 0 : prevIndex + 1
    );
  };

  // Modal handlers
  const openModal = (index) => {
    setModalImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const goToPreviousModal = () => {
    setModalImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNextModal = () => {
    setModalImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Handle keyboard navigation in modal
  React.useEffect(() => {
    const handleKeyPress = (e) => {
      if (isModalOpen) {
        if (e.key === "ArrowLeft") goToPreviousModal();
        if (e.key === "ArrowRight") goToNextModal();
        if (e.key === "Escape") closeModal();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isModalOpen]);

  if (!images || images.length === 0) {
    return <div className="gallery-no-images">No images available</div>;
  }

  return (
    <div className="reusable-gallery">
      {/* Gallery Header */}
      <div className="gallery-header">
        <h2 className="gallery-heading">{heading}</h2>
        {description && <p className="gallery-description">{description}</p>}
      </div>

      {/* Gallery Carousel */}
      <div className="gallery-carousel-container">
        <button
          className="gallery-nav-btn gallery-nav-prev"
          onClick={goToPrevious}
          disabled={currentIndex === 0}
          aria-label="Previous images"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </button>

        <div className="gallery-carousel">
          <div
            className="gallery-track"
            style={{ transform: `translateX(-${currentIndex * 25}%)` }}
          >
            {images.map((image, index) => (
              <div
                key={index}
                className="gallery-slide"
                onClick={() => openModal(index)}
              >
                <img
                  src={image.url || image}
                  alt={image.alt || `Gallery image ${index + 1}`}
                  className="gallery-image"
                  loading="lazy"
                />
                <div className="gallery-overlay">
                  <div className="gallery-zoom-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.5 14h-.79l-.28-.27A6.5 6.5 0 1 0 13 15.5l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14z" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          className="gallery-nav-btn gallery-nav-next"
          onClick={goToNext}
          disabled={currentIndex >= images.length - 4}
          aria-label="Next images"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </button>
      </div>

      {/* Gallery Indicators */}
      <div className="gallery-indicators">
        {Array.from({ length: Math.ceil(images.length / 4) }).map(
          (_, index) => (
            <button
              key={index}
              className={`gallery-indicator ${
                Math.floor(currentIndex / 4) === index ? "active" : ""
              }`}
              onClick={() => setCurrentIndex(index * 4)}
              aria-label={`Go to slide group ${index + 1}`}
            />
          )
        )}
      </div>

      {/* Image Count */}
      <div className="gallery-count">
        {images.length} {images.length === 1 ? "Image" : "Images"}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="gallery-modal" onClick={closeModal}>
          <div
            className="gallery-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="gallery-modal-close"
              onClick={closeModal}
              aria-label="Close modal"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>

            <button
              className="gallery-modal-nav gallery-modal-prev"
              onClick={goToPreviousModal}
              aria-label="Previous image"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </button>

            <img
              src={images[modalImageIndex]?.url || images[modalImageIndex]}
              alt={
                images[modalImageIndex]?.alt ||
                `Gallery image ${modalImageIndex + 1}`
              }
              className="gallery-modal-image"
            />

            <button
              className="gallery-modal-nav gallery-modal-next"
              onClick={goToNextModal}
              aria-label="Next image"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </button>

            <div className="gallery-modal-counter">
              {modalImageIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReusableGallery;
